/* eslint-disable no-spaced-func */
/** @jsx jsx */

import { FormattedMessage } from 'react-intl';
import { ComponentProps, Fragment, ReactNode, useState } from 'react';
import {
  BetaMenuTriggerItem,
  BetaMenuTriggerList,
  BetaMenuTriggerPopover,
  BetaMenuTriggerRoot,
  Button,
  IconButton,
  ModalDialogRoot,
  ModalDialogHeader,
  ModalDialogBody,
  ModalDialogFooter,
  Spacer,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

const styleRoot = css`
  position: relative;
  white-space: nowrap;
  display: flex;
  justify-content: flex-end;
`;

const styleModalWrapper = css`
  text-align: initial;
`;

type Item = {
  /** Will trigger a confirmation when the option is chosen */
  isConfirmationRequired?: boolean;
  /** Renders a disabled option */
  isDisabled?: boolean;
  /** Indicates if this item is a dangerous action (e.g., Delete) */
  isDanger?: boolean;
  /** Content to be rendered in the menu item */
  content: ReactNode;
  /** Optional text to be displayed in the confirmation modal body */
  modalTextContent?: string;
  /** Optional text to be displayed in the confirmation modal title */
  modalTitle?: string;
  /** Callback that handles the clicked option */
  onChoose?: () => void;
  /** Key value of each option*/
  value: string;
};

type Props = {
  /** Modal confirmation button text */
  confirmationText?: string;
  /** Icon that triggers the dropdown. The default is `navCogFill` */
  iconName?: ComponentProps<typeof IconButton>['name'];
  /** Array of actions to be rendered */
  items: Item[];
};

/** Renders a menu toggle with a gear icon. Clicking opens a menu of items. */
export default function ActionsDropdownMenu({
  confirmationText = 'Confirm',
  iconName = 'navMoreHoriz',
  items,
}: Props) {
  const [itemPendingConfirmation, setItemPendingConfirmation] = useState<
    Item | undefined
  >();

  function handleItemAction(item: Item) {
    if (item.isDisabled) {
      return;
    }
    if (item.isConfirmationRequired) {
      setItemPendingConfirmation(item);
      return;
    }
    if (item.onChoose) {
      item.onChoose();
    }
  }

  function modalCancel() {
    setItemPendingConfirmation(undefined);
  }

  function modalConfirm() {
    if (itemPendingConfirmation?.onChoose) {
      itemPendingConfirmation.onChoose();
    }
    setItemPendingConfirmation(undefined);
  }

  return (
    <Fragment>
      {itemPendingConfirmation && (
        <div css={styleModalWrapper}>
          <ModalDialogRoot
            isOpen={!!itemPendingConfirmation}
            onOpenChange={open => {
              if (!open) modalCancel();
            }}
          >
            <ModalDialogHeader>
              {itemPendingConfirmation.modalTitle}
            </ModalDialogHeader>
            <ModalDialogBody>
              {itemPendingConfirmation.modalTextContent}
            </ModalDialogBody>
            <Spacer height="10px" />
            <ModalDialogFooter>
              <Button onPress={modalCancel} variant="secondary">
                <FormattedMessage
                  defaultMessage="Cancel"
                  id="buttons-cancel-confirmation"
                />
              </Button>
              <Button onPress={modalConfirm} variant="primary">
                {confirmationText}
              </Button>
            </ModalDialogFooter>
          </ModalDialogRoot>
        </div>
      )}
      <div css={styleRoot}>
        <BetaMenuTriggerRoot>
          <IconButton
            aria-label="Options toggler"
            name={iconName}
            size="small"
            variant="ghost"
          />
          <BetaMenuTriggerPopover>
            <BetaMenuTriggerList aria-label="Options menu">
              {items.map(item => (
                <BetaMenuTriggerItem
                  isDisabled={item.isDisabled}
                  key={item.value}
                  onAction={() => handleItemAction(item)}
                  variant={item.isDanger ? 'danger' : 'primary'}
                >
                  {item.content}
                </BetaMenuTriggerItem>
              ))}
            </BetaMenuTriggerList>
          </BetaMenuTriggerPopover>
        </BetaMenuTriggerRoot>
      </div>
    </Fragment>
  );
}
